import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';
import AppQuickAction from '../../../../components/AppQuickAction';

export default function ManualPunchTable({
  records = [],
  isLoading = false,
  onApprove,
  onReject,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Manual Punch Records...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">touch_app</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No entries meet selected criteria</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Either there are no entries in the selected module or you have selected invalid filter conditions.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Date Time</th>
              <th className="px-5 py-3.5">Reason</th>
              <th className="px-5 py-3.5">Remarks</th>
              <th className="px-5 py-3.5 text-center">Status</th>
              <th className="px-5 py-3.5 text-center">Quick Action</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={item.id}
                onClick={() => navigate(`/payroll/manual-punch/entry?id=${item.id}`)}
                className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
              >
                {/* Name */}
                <td className="px-5 py-3.5 font-bold text-on-surface">
                  <div className="flex flex-col">
                    <span className="font-bold text-on-surface group-hover:text-primary transition-colors">
                      {item.employee}
                    </span>
                    <span className="text-[11px] font-normal text-on-surface-variant">
                      {item.employeeId}
                    </span>
                  </div>
                </td>

                {/* Date Time */}
                <td className="px-5 py-3.5 text-xs font-semibold text-on-surface whitespace-nowrap">
                  {item.dateTimeDisplay || `${item.date} ${item.time}`}
                </td>

                {/* Reason */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  <span className="inline-flex items-center rounded-md bg-surface-container-high px-2.5 py-1 text-xs font-semibold text-on-surface">
                    {item.reason}
                  </span>
                </td>

                {/* Remarks */}
                <td className="px-5 py-3.5 text-xs text-on-surface-variant max-w-xs truncate">
                  {item.remarks || '-'}
                </td>

                {/* Status Badge */}
                <td className="px-5 py-3.5 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                      item.status === 'Approved'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : item.status === 'Rejected'
                        ? 'bg-rose-500/10 text-rose-600'
                        : 'bg-amber-500/10 text-amber-600'
                    }`}
                  >
                    {item.status}
                  </span>
                </td>

                {/* Quick Action Column */}
                <td
                  className="px-5 py-3.5 text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppQuickAction
                    status={item.status}
                    onApprove={() => onApprove?.(item.id, item.employee)}
                    onReject={() => onReject?.(item.id, item.employee)}
                    approveTitle="Quick Approve Punch"
                    rejectTitle="Quick Reject Punch"
                  />
                </td>

                {/* Actions (Right) */}
                <td
                  className="px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'edit', label: 'Edit Punch', icon: 'edit' },
                      ...(item.status === 'Pending'
                        ? [
                            { key: 'approve', label: 'Approve', icon: 'check_circle' },
                            { key: 'reject', label: 'Reject', icon: 'cancel' },
                          ]
                        : []),
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/payroll/manual-punch/entry?id=${item.id}`);
                      } else if (key === 'approve') {
                        onApprove?.(item.id, item.employee);
                      } else if (key === 'reject') {
                        onReject?.(item.id, item.employee);
                      } else if (key === 'delete') {
                        onDelete?.(item.id, item.employee);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
